import type { ScriptElement } from '@/lib/schema.types';

import { CalloutElement } from '../canvas/elements/CalloutElement';
import { ChipElement } from '../canvas/elements/ChipElement';
import { EmbedElement } from '../canvas/elements/EmbedElement';
import { IconElement } from '../canvas/elements/IconElement';
import { ImageElement } from '../canvas/elements/ImageElement';
import { LatexElement } from '../canvas/elements/LatexElement';
import { McqElement } from '../canvas/elements/McqElement';
import { MermaidElement } from '../canvas/elements/MermaidElement';
import { ShapeElement } from '../canvas/elements/ShapeElement';
import { ThreeDElement } from '../canvas/elements/ThreeDElement';
import { VideoElement } from '../canvas/elements/VideoElement';

/**
 * Read-only stage version of an element. We bypass the editor wrappers
 * (drag handles, Tiptap editing, selection rings) and render just the visual.
 */
export function StageElement({
  element,
  fill,
}: {
  element: ScriptElement;
  /** Set to true when the element should fill its parent (freeform stage). */
  fill?: boolean;
}) {
  switch (element.type) {
    case 'text':
      return <StageText element={element} />;
    case 'image':
      return <ImageElement element={element} fill={fill} />;
    case 'shape':
      return <ShapeElement element={element} />;
    case 'mcq':
      return <McqElement element={element} />;
    case 'chip':
      return <ChipElement element={element} />;
    case 'video':
      return <VideoElement element={element} />;
    case 'embed':
      return <EmbedElement element={element} isSelected={false} />;
    case '3d':
      return <ThreeDElement element={element} fill={fill} />;
    case 'latex':
      return <LatexElement element={element} fill={fill} />;
    case 'icon':
      return <IconElement element={element} />;
    case 'callout':
      return <CalloutElement element={element} />;
    case 'mermaid':
      return <MermaidElement element={element} fill={fill} />;
    default:
      return null;
  }
}

function StageText({ element }: { element: ScriptElement }) {
  const html = typeof element.content === 'string' ? element.content : '';
  const style: React.CSSProperties = {
    fontSize: (element.style?.fontSize as number | undefined) ?? 18,
    fontWeight: (element.style?.fontWeight as number | undefined) ?? 400,
    color: (element.style?.color as string | undefined) ?? '#1a1a2e',
    fontFamily: (element.style?.fontFamily as string | undefined) ?? undefined,
    textAlign: (element.style?.textAlign as React.CSSProperties['textAlign']) ?? 'left',
    lineHeight: 1.4,
  };
  if (html.trim().startsWith('<')) {
    return <div style={style} className="w-full" dangerouslySetInnerHTML={{ __html: html }} />;
  }
  return (
    <div style={style} className="w-full whitespace-pre-wrap">
      {html}
    </div>
  );
}
